import React, { MouseEventHandler, ReactNode } from 'react';
import { Button as SherpaButton } from '@bshgroup/component-button';
import { ClassNames } from '@emotion/react';
import { Link } from 'gatsby-plugin-intl';
import Icon from '../Icon/Icon';

export type Alignment = 'left' | 'center' | 'right' | 'stretch';

export interface ButtonProps {
  /**
   * Button label
   */
  children?: ReactNode;
  /**
   * URL to which the button should lead to
   */
  link?: string;
  /**
   * URL leads to an external page and is not within the Gatsby page
   */
  linkExternal?: boolean;
  /**
   * On click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /**
   * Button alignment, stretch takes up the full width
   */
  alignment?: Alignment;
  /**
   * Display variant
   */
  variant?: 'default' | 'prominent' | 'subtle';
  /**
   * Button is disabled
   */
  disabled?: boolean;
  /**
   * Icon to include, either string to take from the `Icon` component or custom icon element
   */
  icon?: ReactNode;
  /**
   * Color of the icon
   */
  iconColor?: string;
  /**
   * Button type attribute value
   */
  buttonType?: 'button' | 'submit' | 'reset';
  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * The `Button` displays a button with a label and an optional icon.
 */
export default function Button({
  children,
  link,
  linkExternal,
  onClick,
  alignment = 'center',
  variant = 'default',
  disabled = false,
  icon,
  iconColor,
  buttonType,
  size = 'md',
  ...props
}: ButtonProps &
  Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof ButtonProps> &
  Omit<Partial<React.HTMLAttributes<HTMLButtonElement>>, keyof ButtonProps>) {
  // sort for props that need to be assigned to the button element directly
  const buttonProps: Partial<React.HTMLAttributes<HTMLButtonElement>> = {};
  const divProps: Partial<React.HTMLAttributes<HTMLDivElement>> = {};
  Object.entries(props).forEach(([key, value]) => {
    if (['onClick', 'onMouseDown', 'onMouseUp'].includes(key)) {
      buttonProps[key as keyof Partial<React.HTMLAttributes<HTMLButtonElement>>] = value;
    } else {
      divProps[key as keyof Partial<React.HTMLAttributes<HTMLDivElement>>] = value;
    }
  });

  const RenderedButton = () => (
    <ClassNames>
      {({ css }) => (
        <SherpaButton
          size={size}
          variant={variant}
          onClick={onClick}
          disabled={disabled}
          type={buttonType}
          className={css({
            '& > div': { display: children || icon ? 'flex' : 'none', alignItems: 'center', gap: '0.5rem' },
          })}
          {...buttonProps}
        >
          {typeof icon === 'string' ? <Icon value={icon} color={iconColor} size={size} /> : icon}
          {children}
        </SherpaButton>
      )}
    </ClassNames>
  );

  return (
    <div
      css={{
        textAlign: alignment === 'stretch' ? 'center' : alignment,
        ...(alignment === 'stretch' ? { button: { width: '100%' } } : {}),
      }}
      {...divProps}
    >
      {link ? (
        linkExternal ? (
          <a href={link} onClick={onClick}>
            <RenderedButton />
          </a>
        ) : (
          <Link to={link} onClick={onClick}>
            <RenderedButton />
          </Link>
        )
      ) : (
        <RenderedButton />
      )}
    </div>
  );
}
